// Send fleet (GDD 08 screen 3, spec §3.5): origin, mission, target, ships, and a summary with the send button.
import type { ShipType } from '@stars-and-ships/api-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import { ApiRequestError } from '../api/http';
import { queryKeys, useCodex, useFleetOrders, useMap, useSystem } from '../api/queries';
import { cargoCapacity } from '../cargo';
import { DispatchSummary, RouteLine } from '../components/dispatch/DispatchSummary';
import { ShipRows } from '../components/dispatch/ShipRows';
import { TargetPicker } from '../components/dispatch/TargetPicker';
import { type WhenMode, WhenPicker } from '../components/dispatch/WhenPicker';
import { ScheduledOrders } from '../components/fleet/ScheduledOrders';
import type { GameContext } from '../components/GameLayout';
import { parseAmount } from '../components/system/shared';
import {
  Button,
  Checkbox,
  ErrorNotice,
  Notice,
  NumberField,
  Page,
  Panel,
  SegmentedControl,
  Select,
  Spinner,
} from '../components/ui';
import {
  chosenMission,
  type DispatchMission,
  missionBlocker,
  missionOptions,
  shieldWarning,
} from '../dispatch';
import { departureFor, latestArrival, reservedAt, subtractShips } from '../fleetTiming';
import {
  formatNumber,
  formatShips,
  MISSION_LABELS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  SHIP_ORDER,
} from '../labels';
import { useNow } from '../state/live';
import { invalidateMilitary, useSystemNames } from './fleet';

export function DispatchScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const map = useMap(galaxyId);
  const codex = useCodex(galaxyId);
  const name = useSystemNames(galaxyId);
  const now = useNow();

  const [originChoice, setOrigin] = useState(params.get('origin') ?? '');
  const originId = originChoice || me?.systems[0]?.id || '';
  const [targetId, setTarget] = useState(params.get('target') ?? '');
  const origin = useSystem(galaxyId, originId || undefined);
  const originSystem = origin.data?.tier === 'full' ? origin.data.system : undefined;
  const orders = useFleetOrders(galaxyId);
  // Ships held back by timed orders cannot be sent again (G-67).
  const reserved = reservedAt(orders.data, originId);
  const available = subtractShips(
    originSystem?.fleets.find((f) => f.isGarrison)?.ships ?? {},
    reserved
  );
  const plannedHere = (orders.data?.items ?? []).filter((o) => o.originSystemId === originId);
  const systems = map.data?.systems ?? [];
  const originOnMap = systems.find((s) => s.id === originId);
  const target = systems.find((s) => s.id === targetId);

  const [missionChoice, setMission] = useState<DispatchMission | null>(null);
  const mission = chosenMission(missionChoice, target, me);
  const blocker = missionChoice ? missionBlocker(missionChoice, target, me) : null;
  const [counts, setCounts] = useState<Partial<Record<ShipType, string>>>({});
  const [cargoInput, setCargo] = useState({ metal: '', gas: '', supplies: '' });
  const [forceDirect, setForceDirect] = useState(false);
  const [shieldPrompt, setShieldPrompt] = useState(false);
  const presetArrival = params.get('arriveAt');
  const [when, setWhen] = useState<WhenMode>(presetArrival ? 'at' : 'now');
  const [arriveAt, setArriveAt] = useState<number | null>(
    presetArrival ? Date.parse(presetArrival) : null
  );
  const [shared, setShared] = useState(false);

  const ships = SHIP_ORDER.flatMap((shipType) => {
    const count = parseAmount(counts[shipType] ?? '');
    return count > 0 ? [{ shipType, count }] : [];
  });
  const cargo = {
    metal: mission === 'transfer' ? parseAmount(cargoInput.metal) : 0,
    gas: mission === 'transfer' ? parseAmount(cargoInput.gas) : 0,
    supplies: mission === 'transfer' ? parseAmount(cargoInput.supplies) : 0,
  };
  const capacity = cargoCapacity(ships, originSystem?.shipOptions ?? {});
  const cargoTotal = cargo.metal + cargo.gas + cargo.supplies;
  const overCapacity = cargoTotal > capacity;
  // Never shrink what the player typed: more than the garrison holds blocks sending instead.
  const tooMany = ships.filter((s) => s.count > (available[s.shipType] ?? 0));
  const ready = Boolean(
    originId && targetId && targetId !== originId && ships.length > 0 && tooMany.length === 0
  );
  const warnShield = shieldWarning(mission, target, me, now);

  const preview = useQuery({
    queryKey: queryKeys.route(
      galaxyId,
      originId,
      targetId,
      ships.map((s) => `${s.shipType}`).join(','),
      forceDirect
    ),
    queryFn: () =>
      endpoints.routePreview(galaxyId, {
        originSystemId: originId,
        targetSystemId: targetId,
        ships,
        forceDirect,
      }),
    enabled: ready,
  });

  const shareable = (mission === 'attack' || mission === 'raid') && Boolean(me?.alliance);
  const timed = when === 'at';
  const departure =
    timed && arriveAt !== null && preview.data ? departureFor(arriveAt, preview.data.totalS) : null;
  const maxAheadH = codex.data?.travel.timedDispatchMaxAheadH ?? null;
  const latest =
    preview.data && maxAheadH !== null ? latestArrival(now, preview.data.totalS, maxAheadH) : null;
  // The server checks the same window (TOO_LATE / TOO_FAR_AHEAD); the button never sends outside it.
  const outOfWindow =
    timed &&
    (arriveAt === null ||
      departure === null ||
      departure < now ||
      (latest !== null && arriveAt > latest));
  const send = useMutation({
    mutationFn: (confirmShieldLift: boolean): Promise<unknown> => {
      const body = {
        originSystemId: originId,
        targetSystemId: targetId,
        mission,
        ships,
        cargo,
        forceDirect,
        confirmShieldLift,
        shared: shareable && shared,
      };
      return timed
        ? endpoints.placeFleetOrder(galaxyId, {
            ...body,
            arriveAt: new Date(arriveAt as number).toISOString(),
          })
        : endpoints.dispatch(galaxyId, body);
    },
    onSuccess: () => {
      invalidateMilitary(queryClient, galaxyId);
      navigate(`/g/${galaxyId}/fleet`);
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === 'SHIELD_WOULD_LIFT') setShieldPrompt(true);
    },
  });

  const route = !ready ? (
    tooMany.length > 0 ? (
      <p data-too-many-ships className="text-sm text-danger">
        Only{' '}
        {formatShips(
          Object.fromEntries(tooMany.map((s) => [s.shipType, available[s.shipType] ?? 0]))
        )}{' '}
        in {name(originId)}.
      </p>
    ) : (
      <p className="text-sm text-muted">Choose a target and at least one ship.</p>
    )
  ) : preview.isPending ? (
    <Spinner label="Plotting the route…" />
  ) : preview.error ? (
    <ErrorNotice error={preview.error} />
  ) : preview.data ? (
    <RouteLine preview={preview.data} name={name} />
  ) : null;

  const cargoLine =
    ships.length > 0 && (mission === 'transfer' || mission === 'raid')
      ? {
          text:
            (mission === 'transfer'
              ? `Cargo ${formatNumber(cargoTotal)} / ${formatNumber(capacity)}`
              : `Loot capacity ${formatNumber(capacity)}`) +
            (capacity === 0
              ? ' · only Transports carry resources'
              : overCapacity
                ? ' · the selected Transports cannot carry that much'
                : ''),
          over: overCapacity,
        }
      : null;

  return (
    <Page>
      <Link
        to={`/g/${galaxyId}/fleet`}
        className="inline-flex items-center gap-1 text-sm text-info"
      >
        <ArrowLeft className="size-4" aria-hidden /> Fleets
      </Link>
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Send fleet
      </h1>

      <Panel title="Route">
        <div className="space-y-3">
          <Select
            label="From"
            aria-label="Origin system"
            value={originId}
            onChange={(e) => {
              setOrigin(e.target.value);
              setCounts({});
            }}
          >
            {me?.systems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <TargetPicker
            systems={systems}
            origin={originOnMap}
            me={me}
            targetId={targetId}
            onChoose={setTarget}
            // Reinforce and Transfer narrow the list, also when the preset target chose them.
            mission={mission === 'reinforce' || mission === 'transfer' ? mission : undefined}
          />
          <SegmentedControl
            label="Mission"
            value={mission}
            options={missionOptions(target, me)}
            onChange={setMission}
          />
          {mission === 'attack' || mission === 'raid' ? (
            <p className="text-xs text-faint">
              The defender sees the arrival time as soon as your fleet leaves the gate network.
            </p>
          ) : null}
          {blocker ? (
            <p className="text-xs text-muted">
              {blocker} — sending as {MISSION_LABELS[mission]} instead.
            </p>
          ) : null}
          <Checkbox
            label="Direct travel only (skip your warp gates)"
            checked={forceDirect}
            onChange={(e) => setForceDirect(e.target.checked)}
          />
          <WhenPicker
            mode={when}
            onMode={setWhen}
            arriveAt={arriveAt}
            onArriveAt={setArriveAt}
            totalS={preview.data?.totalS ?? null}
            maxAheadH={maxAheadH}
            now={now}
          />
          {shareable ? (
            <Checkbox
              label="Share with alliance"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
            />
          ) : null}
        </div>
      </Panel>

      <Panel title="Ships">
        {origin.isPending ? <Spinner /> : null}
        {SHIP_ORDER.every((t) => (available[t] ?? 0) === 0) && !origin.isPending ? (
          <p className="text-sm text-muted">No ships stationed in {name(originId)}.</p>
        ) : null}
        <ShipRows
          available={available}
          reserved={reserved}
          counts={counts}
          onChange={(type, value) => setCounts((prev) => ({ ...prev, [type]: value }))}
        />
        {mission === 'transfer' ? (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {RESOURCE_KEYS.map((key) => (
              <NumberField
                key={key}
                label={`${RESOURCE_LABELS[key]} cargo`}
                aria-label={`Cargo ${RESOURCE_LABELS[key]}`}
                min={0}
                value={cargoInput[key]}
                onChange={(e) => setCargo((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            ))}
          </div>
        ) : null}
      </Panel>

      {plannedHere.length > 0 ? (
        <Panel title={`Planned from ${name(originId)}`}>
          <ScheduledOrders
            galaxyId={galaxyId}
            orders={plannedHere}
            name={name}
            now={now}
            onChanged={() => invalidateMilitary(queryClient, galaxyId)}
            canShare={Boolean(me?.alliance)}
          />
        </Panel>
      ) : null}

      <DispatchSummary route={route} cargo={cargoLine} shieldWarning={warnShield}>
        {send.error &&
        !(send.error instanceof ApiRequestError && send.error.code === 'SHIELD_WOULD_LIFT') ? (
          <ErrorNotice error={send.error} />
        ) : null}
        {shieldPrompt && !warnShield ? (
          <Notice tone="warning">
            <p>Attacking another commander lifts your beginner shield for good.</p>
            <Button
              className="mt-2"
              variant="danger"
              pending={send.isPending}
              onClick={() => {
                setShieldPrompt(false);
                send.mutate(true);
              }}
            >
              Lift shield and send
            </Button>
          </Notice>
        ) : null}
        <Button
          className="w-full"
          variant={warnShield ? 'danger' : 'primary'}
          disabled={
            !ready || overCapacity || missionBlocker(mission, target, me) !== null || outOfWindow
          }
          pending={send.isPending}
          onClick={() => send.mutate(warnShield)}
        >
          {timed
            ? warnShield
              ? 'Lift shield and schedule'
              : 'Schedule fleet'
            : warnShield
              ? 'Lift shield and send'
              : 'Send fleet'}
        </Button>
      </DispatchSummary>
    </Page>
  );
}
